import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import type { Tone } from './feedback';
import { Icon, type IconName } from './Icon';

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
}

interface ToastApi {
  show(message: string, tone?: Tone): void;
}

const Ctx = createContext<ToastApi>({ show: () => {} });

const ICON: Record<Tone, IconName> = {
  neutral: 'info',
  info: 'info',
  success: 'checkCircle',
  warning: 'alert',
  danger: 'alertCircle',
  brand: 'info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const show = useCallback((message: string, tone: Tone = 'neutral') => {
    const id = next.current++;
    setItems((xs) => [...xs.slice(-2), { id, message, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4000);
  }, []);
  const api = useMemo(() => ({ show }), [show]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex flex-col items-center gap-2 px-4 lg:bottom-6">
            {items.map((t) => (
              <div
                key={t.id}
                className={cx(
                  'animate-toast-in pointer-events-auto flex max-w-md items-center gap-2.5 rounded-xl border border-white/12 px-4 py-3 text-sm font-medium shadow-elevated',
                  t.tone === 'danger' ? 'bg-danger text-white' : t.tone === 'success' ? 'bg-success text-white' : 'bg-[#181816] text-white',
                )}
              >
                <Icon name={ICON[t.tone]} size={18} />
                {t.message}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(Ctx);
}
